import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/admin-panel.css';
import { getImageUrl, API_BASE_URL } from '../lib/api';

const API_URL = API_BASE_URL;
const MAX_FILE_SIZE_MB = parseInt(process.env.REACT_APP_MAX_FILE_SIZE_MB) || 3;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024; // Convert MB to bytes

const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Form states
  const [productForm, setProductForm] = useState({ name: '', description: '', category: '', image: null });
  const [editingProduct, setEditingProduct] = useState(null);

  // Check auth status on mount
  useEffect(() => {
    checkAuth();
  }, []);

  // Fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchProducts();
      fetchCategories();
    }
  }, [isAuthenticated]);

  const checkAuth = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/status`, { withCredentials: true });
      setIsAuthenticated(res.data.isAuthenticated);
    } catch (err) {
      console.error('Auth check failed:', err);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/auth/login`, { password }, { withCredentials: true });
      if (res.data.success) {
        setIsAuthenticated(true);
        setPassword('');
        setError('');
      }
    } catch (err) {
      setError('Invalid password. Please try again.');
      setPassword('');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`, {}, { withCredentials: true });
      setIsAuthenticated(false);
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API_URL}/products`);
      setProducts(res.data);
    } catch (err) {
      setError('Failed to fetch products');
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/categories`);
      setCategories(res.data);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    
    // Validate file size before submitting
    if (productForm.image && productForm.image.size > MAX_FILE_SIZE) {
      setError(`Image size must be less than ${MAX_FILE_SIZE_MB}MB. Your file is ${(productForm.image.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    const formData = new FormData();
    formData.append('name', productForm.name);
    formData.append('description', productForm.description);
    formData.append('category', productForm.category);
    if (productForm.image) {
      formData.append('image', productForm.image);
    }

    try {
      if (editingProduct) {
        await axios.put(`${API_URL}/products/${editingProduct._id}`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setSuccess('Product updated successfully!');
      } else {
        await axios.post(`${API_URL}/products`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setSuccess('Product added successfully!');
      }
      
      // Reset form
      setProductForm({ name: '', description: '', category: '', image: null });
      setEditingProduct(null);
      setShowNewCategoryInput(false);
      
      // Fetch updated data and wait for it to complete
      await Promise.all([fetchProducts(), fetchCategories()]);
      
      // Switch tab only after data is loaded
      setActiveTab('view');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    setLoading(true);
    try {
      await axios.delete(`${API_URL}/products/${id}`, { withCredentials: true });
      setSuccess('Product deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
      fetchProducts();
    } catch (err) {
      setError('Failed to delete product');
    } finally {
      setLoading(false);
    }
  };

  const editProduct = (product) => {
    setEditingProduct(product);
    const categoryName = product.category?.name || '';
    const categoryExists = categories.some(cat => cat.name.toLowerCase() === categoryName.toLowerCase());

    setProductForm({
      name: product.name,
      description: product.description,
      category: categoryExists ? categoryName : '',
      image: null
    });
    setShowNewCategoryInput(!categoryExists && categoryName !== '');
    setActiveTab('add');
  };

  const deleteCategory = async (id, categoryName) => {
    const productsCount = products.filter(p => p.category?._id === id).length;
    const message = productsCount > 0 
      ? `Are you sure you want to delete "${categoryName}"? This will also delete ${productsCount} product(s) under this category.`
      : `Are you sure you want to delete "${categoryName}"?`;
    
    if (!window.confirm(message)) return;
    
    setLoading(true);
    try {
      const res = await axios.delete(`${API_URL}/categories/${id}`, { withCredentials: true });
      setSuccess(res.data.message + (res.data.deletedProductsCount > 0 ? ` (${res.data.deletedProductsCount} products deleted)` : ''));
      setTimeout(() => setSuccess(''), 3000);
      await Promise.all([fetchProducts(), fetchCategories()]);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete category');
    } finally {
      setLoading(false);
    }
  };

  // Filter products based on search term
  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.category?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate statistics
  const stats = {
    totalProducts: products.length,
    totalCategories: categories.length,
    recentProducts: products.filter(p => {
      const productDate = new Date(p.createdAt);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return productDate > weekAgo;
    }).length
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-login">
        <div className="admin-login-background">
          <div className="admin-login-circle circle-1"></div>
          <div className="admin-login-circle circle-2"></div>
          <div className="admin-login-circle circle-3"></div>
        </div>
        <div className="admin-login-card">
          <div className="admin-login-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1>Admin Panel</h1>
          <p className="admin-login-subtitle">Enter your credentials to access the dashboard</p>
          <form onSubmit={handleLogin} className="admin-login-form">
            <div className="admin-input-group">
              <div className="admin-input-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
              </div>
              <input
                type="password"
                placeholder="Enter admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="admin-input"
                autoFocus
                disabled={loginLoading}
              />
            </div>
            {error && (
              <div className="admin-error-message">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
                {error}
              </div>
            )}
            <button type="submit" className="admin-login-btn" disabled={loginLoading}>
              {loginLoading ? (
                <>
                  <span className="admin-spinner"></span>
                  Authenticating...
                </>
              ) : (
                <>
                  <span>Sign In</span>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <div className="admin-header-content">
          <h1>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />
            </svg>
            Sitcorp Admin Dashboard
          </h1>
          <p className="admin-header-subtitle">Manage your products and categories</p>
        </div>
        <button onClick={handleLogout} className="admin-logout-btn">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          Logout
        </button>
      </div>

      {(error || success) && (
        <div className={`admin-message-banner ${error ? 'error' : 'success'}`}>
          <svg viewBox="0 0 24 24" fill="currentColor">
            {error ? (
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            ) : (
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            )}
          </svg>
          {error || success}
          <button onClick={() => { setError(''); setSuccess(''); }} className="message-close">×</button>
        </div>
      )}

      <div className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />
          </svg>
          Dashboard
        </button>
        <button
          className={`admin-tab ${activeTab === 'add' ? 'active' : ''}`}
          onClick={() => setActiveTab('add')}
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z" />
          </svg>
          {editingProduct ? 'Edit Product' : 'Add Product'}
        </button>
        <button
          className={`admin-tab ${activeTab === 'view' ? 'active' : ''}`}
          onClick={() => setActiveTab('view')}
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M4 6h16v2H4zm0 5h16v2H4zm0 5h16v2H4z" />
          </svg>
          View Products
          <span className="admin-tab-badge">{products.length}</span>
        </button>
        <button
          className={`admin-tab ${activeTab === 'categories' ? 'active' : ''}`}
          onClick={() => setActiveTab('categories')}
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2l-5.5 9h11z M17.5 13c-1.93 0-3.5 1.57-3.5 3.5s1.57 3.5 3.5 3.5 3.5-1.57 3.5-3.5-1.57-3.5-3.5-3.5zm-11 0H2v8h8v-8H6.5z" />
          </svg>
          Categories
          <span className="admin-tab-badge">{categories.length}</span>
        </button>
      </div>

      {activeTab === 'dashboard' && (
        <div className="admin-dashboard">
          <div className="admin-stats-grid">
            <div className="admin-stat-card">
              <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 6h-2.18c.11-.31.18-.65.18-1 0-1.66-1.34-3-3-3-1.05 0-1.96.54-2.5 1.35l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm11 15H4v-2h16v2zm0-5H4V8h5.08L7 10.83 8.62 12 11 8.76l1-1.36 1 1.36L15.38 12 17 10.83 14.92 8H20v6z" />
                </svg>
              </div>
              <div className="stat-content">
                <h3>Total Products</h3>
                <p className="stat-number">{stats.totalProducts}</p>
              </div>
            </div>

            <div className="admin-stat-card">
              <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l-5.5 9h11z M17.5 13c-1.93 0-3.5 1.57-3.5 3.5s1.57 3.5 3.5 3.5 3.5-1.57 3.5-3.5-1.57-3.5-3.5-3.5zm-11 0H2v8h8v-8H6.5z" />
                </svg>
              </div>
              <div className="stat-content">
                <h3>Categories</h3>
                <p className="stat-number">{stats.totalCategories}</p>
              </div>
            </div>

            <div className="admin-stat-card">
              <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
                </svg>
              </div>
              <div className="stat-content">
                <h3>Recent (7 days)</h3>
                <p className="stat-number">{stats.recentProducts}</p>
              </div>
            </div>
          </div>

          <div className="admin-dashboard-content">
            <div className="admin-recent-products">
              <h2>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9z" />
                </svg>
                Recent Products
              </h2>
              {products.length === 0 ? (
                <div className="empty-state">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 6h-2.18c.11-.31.18-.65.18-1 0-1.66-1.34-3-3-3-1.05 0-1.96.54-2.5 1.35l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1z" />
                  </svg>
                  <h3>No products yet</h3>
                  <p>Start by adding your first product</p>
                  <button onClick={() => setActiveTab('add')} className="admin-btn">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                    </svg>
                    Add Product
                  </button>
                </div>
              ) : (
                <div className="recent-products-grid">
                  {products.slice(0, 6).map(product => (
                    <div key={product._id} className="recent-product-card">
                      {product.image && (
                        <img src={getImageUrl(product.image, new Date(product.uploadedAt).getTime())} alt={product.name} />
                      )}
                      <div className="recent-product-content">
                        <h4>{product.name}</h4>
                        <span className="product-category">{product.category?.name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'add' && (
        <div className="admin-content-single">
          <div className="admin-form-section">
            <div className="form-header">
              <h2>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                </svg>
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </h2>
              <p>Fill in the details below to {editingProduct ? 'update' : 'add'} a product</p>
            </div>
            <form onSubmit={handleProductSubmit} className="admin-form">
              <div className="admin-form-group">
                <label>
                  Product Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={productForm.name}
                  onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                  required
                  className="admin-input"
                  placeholder="Enter product name"
                />
              </div>
              <div className="admin-form-group">
                <label>Description</label>
                <textarea
                  value={productForm.description}
                  onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                  className="admin-input admin-textarea"
                  rows="4"
                  placeholder="Enter product description"
                />
              </div>
              <div className="admin-form-group">
                <label>
                  Category <span className="required">*</span>
                </label>
                {!showNewCategoryInput ? (
                  <div className="category-select-wrapper">
                    <select
                      value={productForm.category}
                      onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                      required
                      className="admin-input"
                    >
                      <option value="">Select a category</option>
                      {categories.map(cat => (
                        <option key={cat._id} value={cat.name}>{cat.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => {
                        setShowNewCategoryInput(true);
                        setProductForm({ ...productForm, category: '' });
                      }}
                      className="admin-btn-link"
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                      </svg>
                      Add New Category
                    </button>
                  </div>
                ) : (
                  <div className="category-input-wrapper">
                    <input
                      type="text"
                      value={productForm.category}
                      onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                      required
                      className="admin-input"
                      placeholder="Enter new category name"
                    />
                    <small className="form-hint">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
                      </svg>
                      Category will be auto-created if it doesn't exist
                    </small>
                    <button
                      type="button"
                      onClick={() => {
                        setShowNewCategoryInput(false);
                        setProductForm({ ...productForm, category: '' });
                      }}
                      className="admin-btn-link"
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                      </svg>
                      Select Existing Category
                    </button>
                  </div>
                )}
              </div>
              <div className="admin-form-group">
                <label>
                  Product Image <span className="required">*</span>
                </label>
                <div className="file-input-wrapper">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file && file.size > MAX_FILE_SIZE) {
                        setError(`Image size must be less than ${MAX_FILE_SIZE_MB}MB. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB`);
                        e.target.value = '';
                      } else {
                        setError('');
                        setProductForm({ ...productForm, image: file });
                      }
                    }}
                    required={!editingProduct}
                    className="admin-input-file"
                    id="productImage"
                  />
                  <label htmlFor="productImage" className="file-input-label">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
                    </svg>
                    <span>{productForm.image ? productForm.image.name : 'Choose an image file'}</span>
                  </label>
                </div>
                <small className="form-hint">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
                  </svg>
                  Maximum file size: {MAX_FILE_SIZE_MB}MB. Supported formats: JPEG, PNG, WEBP
                  {productForm.image && (
                    <span style={{marginLeft: '10px', color: productForm.image.size > MAX_FILE_SIZE ? '#f5576c' : '#4facfe'}}>
                      ({(productForm.image.size / 1024 / 1024).toFixed(2)}MB)
                    </span>
                  )}
                </small>
              </div>
              <div className="admin-form-actions">
                <button type="submit" disabled={loading} className="admin-btn admin-btn-primary">
                  {loading ? (
                    <>
                      <span className="admin-spinner"></span>
                      {editingProduct ? 'Updating...' : 'Adding...'}
                    </>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      {editingProduct ? 'Update Product' : 'Add Product'}
                    </>
                  )}
                </button>
                {editingProduct && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingProduct(null);
                      setProductForm({ name: '', description: '', category: '', image: null });
                      setShowNewCategoryInput(false);
                    }}
                    className="admin-btn admin-btn-secondary"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                    </svg>
                    Cancel Edit
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {activeTab === 'view' && (
        <div className="admin-content-single">
          <div className="admin-list-section">
            <div className="list-header">
              <h2>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 6h-2.18c.11-.31.18-.65.18-1 0-1.66-1.34-3-3-3-1.05 0-1.96.54-2.5 1.35l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2z" />
                </svg>
                All Products
              </h2>
              <div className="search-wrapper">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
            </div>
            {filteredProducts.length === 0 ? (
              <div className="empty-state">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                </svg>
                <h3>{searchTerm ? 'No products found' : 'No products yet'}</h3>
                <p>{searchTerm ? `No results for "${searchTerm}"` : 'Start by adding your first product'}</p>
                {!searchTerm && (
                  <button onClick={() => setActiveTab('add')} className="admin-btn">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                    </svg>
                    Add Product
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="results-count">
                  Showing {filteredProducts.length} of {products.length} products
                </div>
                <div className="admin-products-grid">
                  {filteredProducts.map(product => (
                    <div key={product._id} className="admin-product-card">
                      <div className="product-card-image">
                        {product.image && (
                          <img src={getImageUrl(product.image, new Date(product.uploadedAt).getTime())} alt={product.name} />
                        )}
                        <div className="product-card-overlay">
                          <button onClick={() => editProduct(product)} className="overlay-btn">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                            </svg>
                            Edit
                          </button>
                          <button onClick={() => deleteProduct(product._id)} className="overlay-btn delete">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                            </svg>
                            Delete
                          </button>
                        </div>
                      </div>
                      <div className="product-card-content">
                        <h3>{product.name}</h3>
                        <span className="product-category-badge">{product.category?.name}</span>
                        <p>{product.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {activeTab === 'categories' && (
        <div className="admin-content-single">
          <div className="admin-list-section">
            <div className="list-header">
              <h2>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l-5.5 9h11z M17.5 13c-1.93 0-3.5 1.57-3.5 3.5s1.57 3.5 3.5 3.5 3.5-1.57 3.5-3.5-1.57-3.5-3.5-3.5zm-11 0H2v8h8v-8H6.5z" />
                </svg>
                All Categories
              </h2>
            </div>
            {categories.length === 0 ? (
              <div className="empty-state">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l-5.5 9h11z M17.5 13c-1.93 0-3.5 1.57-3.5 3.5s1.57 3.5 3.5 3.5 3.5-1.57 3.5-3.5-1.57-3.5-3.5-3.5zm-11 0H2v8h8v-8H6.5z" />
                </svg>
                <h3>No categories yet</h3>
                <p>Categories are created automatically when you add products</p>
              </div>
            ) : (
              <>
                <div className="results-count">
                  {categories.length} {categories.length === 1 ? 'category' : 'categories'}
                </div>
                <div className="admin-products-grid">
                  {categories.map(category => {
                    const productsCount = products.filter(p => p.category?._id === category._id).length;
                    return (
                      <div key={category._id} className="admin-product-card">
                        <div className="product-card-image" style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}}>
                          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'white', fontSize: '48px', fontWeight: 'bold'}}>
                            {category.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="product-card-overlay">
                            <button onClick={() => deleteCategory(category._id, category.name)} className="overlay-btn delete">
                              <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                              </svg>
                              Delete
                            </button>
                          </div>
                        </div>
                        <div className="product-card-content">
                          <h3>{category.name}</h3>
                          <span className="product-category-badge">{productsCount} {productsCount === 1 ? 'product' : 'products'}</span>
                          {category.description && <p>{category.description}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;